/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { LayoutModule } from '@angular/cdk/layout';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { CommonModule, DatePipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyProgressBarModule } from '@angular/material/legacy-progress-bar';
import { MatLegacyProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacySelectModule, MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatStepperModule } from '@angular/material/stepper';
import { TooltipModule } from 'shared';
import { TachoBalloonDrivingInfoModule, TachoMessageSectionModule } from 'tacho';
import {
  ContextMenuModule,
  GoogleMapsLightModule,
  NotificationsModule,
  RadioButtonModule,
  SnackbarModule,
  TabsModule
} from '../core/components';
import { DeviceHealthCheckModule } from '../device-health-check';
import { GeofenceModule } from '../geofence';
import { LazyLoaderModule } from '../lazy-loader/lazy-loader.module';
import { DeviceHealthCategoryLocalizePipe, DeviceHealthErrorLocalizePipe, DeviceHealthIssueTooltip, SharedModule } from '../shared';
import { SidePanelModule } from '../side-panel';
import {
  AssetBalloonComponent,
  AssetBatteryBalloonSectionComponent,
  AssetMenuContainerComponent,
  BalloonActionsComponent,
  BalloonComponent,
  BalloonHeaderComponent,
  BalloonSectionComponent,
  ClusterBalloonComponent,
  DefaultOverlaysContainerComponent,
  DeviceHealthBalloonSectionComponent,
  DriverMessageComponent,
  DriverSmsMessageComponent,
  EcmFuelInfoBalloonSectionComponent,
  EditWorkOrderComponent,
  GarminBalloonComponent,
  GarminMenuContainerComponent,
  GarminStopHistoryComponent,
  GeofenceMenuContainerComponent,
  GeofenceMenuContentComponent,
  GoogleMapsComponent,
  HarshDrivingEventBalloonBadgeComponent,
  HarshDrivingEventBalloonComponent,
  HarshDrivingEventBalloonTitleComponent,
  HotspotMenuContainerComponent,
  LabelComponent,
  MapContainerComponent,
  MapMenuContainerComponent,
  PlaceBalloonComponent,
  PolylineHoverBalloonComponent,
  ReplayBalloonComponent,
  ReplayBalloonHeaderComponent,
  RoadsideAssistanceMarketingModalComponent,
  RoadsideAssistanceModalComponent,
  SchedulerBalloonSectionComponent,
  SchedulerBalloonSectionItemComponent,
  SendToGarminComponent,
  VehicleIdentificationNumberComponent,
  VehicleMenuContainerComponent,
  VehicleWorkOrderBalloonComponent,
  VideoEventDetailsComponent,
  WorkOrderMenuContainerComponent
} from './components';
import { MapComponent, OverlayBalloonComponent, OverlayMapMenusComponent, VideoRequestModalComponent } from './containers';
import { GoogleMapsGeofenceDrawingDirective } from './directives';
import { MapsIconModule } from './maps-icon.module';
import { MAPS_ROUTES_MODULE } from './maps.routes';
import { PlaceByQueryParamResolver } from './services/resolver/place-resolver';

@NgModule({
  imports: [
    CommonModule,
    MapsIconModule,
    ContextMenuModule,
    NotificationsModule,
    LayoutModule,
    MAPS_ROUTES_MODULE,
    TabsModule,
    TooltipModule,
    RadioButtonModule,
    ReactiveFormsModule,
    FormsModule,
    SnackbarModule,
    GoogleMapsLightModule,
    SharedModule,
    TachoMessageSectionModule,
    TachoBalloonDrivingInfoModule,
    LazyLoaderModule,
    ScrollingModule,
    SidePanelModule,
    DeviceHealthCheckModule,
    GeofenceModule,
    MatLegacyDialogModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatStepperModule,
    MatLegacyButtonModule,
    MatLegacyProgressSpinnerModule,
    MatLegacyProgressBarModule,
    MatIconModule,
    MatLegacyInputModule,
    MatLegacyFormFieldModule,
    MatLegacySelectModule,
    DeviceHealthCategoryLocalizePipe,
    DeviceHealthErrorLocalizePipe,
    DeviceHealthIssueTooltip
  ],
  declarations: [
    GoogleMapsComponent,
    MapContainerComponent,
    DefaultOverlaysContainerComponent,
    MapComponent,
    LabelComponent,
    OverlayMapMenusComponent,
    MapMenuContainerComponent,
    VehicleMenuContainerComponent,
    SendToGarminComponent,
    GeofenceMenuContainerComponent,
    HotspotMenuContainerComponent,
    GeofenceMenuContentComponent,
    DriverMessageComponent,
    DriverSmsMessageComponent,
    GarminMenuContainerComponent,
    GarminStopHistoryComponent,
    WorkOrderMenuContainerComponent,
    BalloonComponent,
    VehicleWorkOrderBalloonComponent,
    BalloonHeaderComponent,
    BalloonActionsComponent,
    OverlayBalloonComponent,
    BalloonSectionComponent,
    PlaceBalloonComponent,
    GarminBalloonComponent,
    EditWorkOrderComponent,
    AssetBalloonComponent,
    AssetMenuContainerComponent,
    RoadsideAssistanceModalComponent,
    VehicleIdentificationNumberComponent,
    HarshDrivingEventBalloonComponent,
    HarshDrivingEventBalloonTitleComponent,
    HarshDrivingEventBalloonBadgeComponent,
    RoadsideAssistanceMarketingModalComponent,
    SchedulerBalloonSectionComponent,
    SchedulerBalloonSectionItemComponent,
    ReplayBalloonComponent,
    PolylineHoverBalloonComponent,
    EcmFuelInfoBalloonSectionComponent,
    AssetBatteryBalloonSectionComponent,
    ClusterBalloonComponent,
    VideoEventDetailsComponent,
    ReplayBalloonHeaderComponent,
    GoogleMapsGeofenceDrawingDirective,
    VideoRequestModalComponent,
    DeviceHealthBalloonSectionComponent
  ],
  providers: [DatePipe, PlaceByQueryParamResolver, { provide: STEPPER_GLOBAL_OPTIONS, useValue: { displayDefaultIndicatorType: false } }]
})
export class MapsModule {}
